import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { projectsAPI } from "../../api/client";
import { buildGanttModel, formatSpan } from "../../utils/ganttModel";
import { formatProjectStatus } from "../../utils/formatProjectStatus";
import GanttView, { pickDefaultZoom } from "./GanttView";
import { buildStageViewRows, formatDateTime } from "./stageRows";
import useOrgCalendar from "./useOrgCalendar";

const PROJECT_COLORS = {
  PENDING: "#f59e0b",
  IN_PROGRESS: "#3b82f6",
  ON_HOLD: "#94a3b8",
  AWAITING_INVOICE: "#8b5cf6",
  COMPLETED: "#22c55e",
};

const NO_FILTER = [];

const SORTS = [
  { value: "start", label: "Start date" },
  { value: "finish", label: "Finish date" },
  { value: "overdue", label: "Overdue first" },
  { value: "name", label: "Name" },
];

const LEGEND = [
  { label: "In Progress", color: PROJECT_COLORS.IN_PROGRESS },
  { label: "Pending", color: PROJECT_COLORS.PENDING },
  { label: "On Hold", color: PROJECT_COLORS.ON_HOLD },
  { label: "Awaiting Invoice", color: PROJECT_COLORS.AWAITING_INVOICE },
  { label: "Completed", color: PROJECT_COLORS.COMPLETED },
  { label: "Projected", className: "legend-projected" },
  { label: "Overdue", className: "legend-overdue" },
  { label: "Holiday", className: "legend-holiday" },
];

const PROJECT_ROW_HEIGHT = 50;

// Roll-up of one project's stage model: where it started, where it is
// expected to finish, and how far through its stages it is.
function summarize(project, model, now) {
  const actualBars = model.rows.flatMap((row) =>
    row.bars.filter((bar) => bar.kind === "actual"),
  );
  const hasProjected = model.rows.some((row) =>
    row.bars.some((bar) => bar.kind === "projected"),
  );
  const start = model.start;
  const end = model.end || start;
  const actualEnd = actualBars.length
    ? new Date(Math.max(...actualBars.map((b) => b.end)))
    : null;
  const overdueBars = actualBars.filter((bar) => bar.overdueMs > 0);
  const counted = model.rows.filter((row) => row.state !== "SKIPPED");
  const done = counted.filter((row) => row.state === "DONE").length;
  const closed = ["COMPLETED", "AWAITING_INVOICE"].includes(project.status);

  return {
    start,
    end,
    actualEnd,
    hasProjected,
    stagesDone: done,
    stagesTotal: counted.length,
    progress: closed
      ? 100
      : counted.length
        ? Math.round((done / counted.length) * 100)
        : 0,
    activeStages: model.rows
      .filter((row) => row.state === "ACTIVE")
      .map((row) => row.stageName),
    overdueCount: overdueBars.length,
    overdueFrom: overdueBars.length
      ? new Date(Math.min(...overdueBars.map((b) => b.expectedEnd)))
      : null,
    worstOverdueMs: overdueBars.length
      ? Math.max(...overdueBars.map((b) => b.overdueMs))
      : 0,
    isLate: !closed && end < now,
  };
}

function ProjectTooltip({ item }) {
  const { project, summary, workflowName } = item;
  return (
    <>
      <div className="gantt-tip-title">{project.name}</div>
      <div
        className="gantt-tip-badge"
        style={{
          background: PROJECT_COLORS[project.status] || "#94a3b8",
          color: "#fff",
        }}
      >
        {formatProjectStatus(project.status)}
      </div>
      <dl>
        <dt>Customer</dt>
        <dd>{project.customerName || "-"}</dd>
        <dt>Workflow</dt>
        <dd>{workflowName || "-"}</dd>
        <dt>Started</dt>
        <dd>{formatDateTime(summary.start)}</dd>
        <dt>{summary.hasProjected ? "Projected finish" : "Finished"}</dt>
        <dd>{formatDateTime(summary.end)}</dd>
        <dt>Progress</dt>
        <dd>
          {summary.stagesDone} of {summary.stagesTotal} stages ·{" "}
          {summary.progress}%
        </dd>
        {summary.activeStages.length > 0 && (
          <>
            <dt>Current</dt>
            <dd>{summary.activeStages.join(", ")}</dd>
          </>
        )}
      </dl>
      {summary.overdueCount > 0 && (
        <div className="gantt-tip-alert">
          {summary.overdueCount} overdue stage
          {summary.overdueCount > 1 ? "s" : ""} · up to{" "}
          {formatSpan(summary.worstOverdueMs)} late
        </div>
      )}
      <div className="gantt-tip-note">
        Click the bar to show or hide its stages.
      </div>
    </>
  );
}

function ProjectLabel({ item, expanded, onToggle }) {
  const { project, summary, workflowName } = item;
  return (
    <>
      <button
        type="button"
        className={`gantt-expand ${expanded ? "is-open" : ""}`}
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`${expanded ? "Collapse" : "Expand"} ${project.name}`}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>
      <div className="gantt-left-text">
        <Link
          className="gantt-project-name"
          to={`/projects/${project.id}?view=gantt`}
          title={project.name}
        >
          {project.name}
        </Link>
        <div className="gantt-stage-meta">
          {[project.customerName, workflowName].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div
        className="gantt-progress"
        title={`${summary.stagesDone} of ${summary.stagesTotal} stages done`}
      >
        <div className="gantt-progress-track">
          <div
            className="gantt-progress-fill"
            style={{
              width: `${summary.progress}%`,
              background: PROJECT_COLORS[project.status] || "#94a3b8",
            }}
          />
        </div>
        <span>{summary.progress}%</span>
      </div>
      {summary.overdueCount > 0 && (
        <span
          className="gantt-overdue-dot"
          title={`${summary.overdueCount} overdue stage(s)`}
        >
          !
        </span>
      )}
    </>
  );
}

// filters mirror the Projects page: an empty list means "no filter on that
// axis", and search matches project, customer or workflow name.
export default function PortfolioGanttChart({ filters = {} }) {
  const {
    search = "",
    statuses = NO_FILTER,
    workflows = NO_FILTER,
    customers = NO_FILTER,
  } = filters;
  const calendar = useOrgCalendar();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sort, setSort] = useState("start");
  const [zoom, setZoom] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    projectsAPI
      .getPortfolioGantt()
      .then((resp) => !cancelled && setData(resp.data))
      .catch(
        (err) =>
          !cancelled &&
          setError(
            err.response?.data?.message ||
              "Failed to load the project timeline",
          ),
      );
    return () => {
      cancelled = true;
    };
  }, []);

  // Every project's stage model, built once per load. Filtering and sorting
  // below only reorder these.
  const portfolio = useMemo(() => {
    if (!data || !calendar) return null;
    const now = new Date();
    const items = data.projects.map((project) => {
      const workflow = data.workflows[project.workflowId];
      const model = buildGanttModel(
        { ...project, workflow: { definition: workflow?.definition } },
        calendar,
        now,
      );
      return {
        project,
        model,
        workflowName: workflow?.name || null,
        summary: summarize(project, model, now),
      };
    });
    return { items, now };
  }, [data, calendar]);

  const visibleItems = useMemo(() => {
    if (!portfolio) return [];
    const query = search.trim().toLowerCase();
    const items = portfolio.items.filter(({ project, workflowName }) => {
      if (statuses.length && !statuses.includes(project.status)) return false;
      if (workflows.length && !workflows.includes(workflowName)) return false;
      if (customers.length && !customers.includes(project.customerName))
        return false;
      if (!query) return true;
      return [project.name, project.customerName, workflowName].some(
        (value) => value && value.toLowerCase().includes(query),
      );
    });
    const byName = (a, b) => a.project.name.localeCompare(b.project.name);
    const comparators = {
      start: (a, b) => a.summary.start - b.summary.start || byName(a, b),
      finish: (a, b) => a.summary.end - b.summary.end || byName(a, b),
      overdue: (a, b) =>
        b.summary.worstOverdueMs - a.summary.worstOverdueMs ||
        a.summary.end - b.summary.end,
      name: byName,
    };
    return items.sort(comparators[sort]);
  }, [portfolio, search, statuses, workflows, customers, sort]);

  const toggle = useCallback(
    (projectId) =>
      setExpanded((current) => {
        const next = new Set(current);
        if (next.has(projectId)) next.delete(projectId);
        else next.add(projectId);
        return next;
      }),
    [],
  );

  const view = useMemo(() => {
    const rows = [];
    const dependencies = [];
    visibleItems.forEach((item) => {
      const { project, summary } = item;
      const isOpen = expanded.has(project.id);
      const color = PROJECT_COLORS[project.status] || "#94a3b8";
      const tooltip = <ProjectTooltip item={item} />;
      const splitAt = summary.actualEnd || summary.start;
      const bars = [];
      if (summary.actualEnd) {
        bars.push({
          id: `${project.id}-actual`,
          kind: "summary",
          start: summary.start,
          end: summary.actualEnd,
          color,
          overdueFrom:
            summary.overdueFrom && summary.overdueFrom < summary.actualEnd
              ? summary.overdueFrom
              : null,
          label: project.name,
          tooltip,
          onClick: () => toggle(project.id),
        });
      }
      if (summary.hasProjected && summary.end > splitAt) {
        bars.push({
          id: `${project.id}-projected`,
          kind: "projected",
          start: splitAt,
          end: summary.end,
          label: summary.actualEnd ? null : project.name,
          tooltip,
          onClick: () => toggle(project.id),
        });
      }
      rows.push({
        key: project.id,
        height: PROJECT_ROW_HEIGHT,
        barHeight: 20,
        className: `gantt-row-project ${isOpen ? "is-open" : ""}`,
        label: (
          <ProjectLabel
            item={item}
            expanded={isOpen}
            onToggle={() => toggle(project.id)}
          />
        ),
        bars,
      });

      if (isOpen) {
        const stageView = buildStageViewRows(item.model, {
          keyPrefix: `${project.id}:`,
          compact: true,
          projectName: project.name,
        });
        rows.push(...stageView.rows);
        dependencies.push(...stageView.dependencies);
      }
    });
    return { rows, dependencies };
  }, [visibleItems, expanded, toggle]);

  const range = useMemo(() => {
    if (!portfolio || !visibleItems.length) return null;
    const { now } = portfolio;
    const min = new Date(Math.min(...visibleItems.map((i) => i.summary.start)));
    const max = new Date(
      Math.max(...visibleItems.map((i) => i.summary.end), now),
    );
    return { min, max, now };
  }, [portfolio, visibleItems]);

  if (error) return <div className="panel gantt-empty">{error}</div>;
  if (!portfolio) {
    return <div className="panel gantt-empty">Loading project timeline...</div>;
  }

  const overdueProjects = visibleItems.filter(
    (i) => i.summary.overdueCount > 0,
  ).length;

  const toolbar = (
    <div className="gantt-filters">
      <select
        className="gantt-select"
        value={sort}
        onChange={(e) => setSort(e.target.value)}
        aria-label="Sort projects"
      >
        {SORTS.map((option) => (
          <option key={option.value} value={option.value}>
            Sort: {option.label}
          </option>
        ))}
      </select>
      <span className="gantt-count">
        {visibleItems.length} project{visibleItems.length === 1 ? "" : "s"}
        {overdueProjects > 0 && (
          <span className="gantt-count-alert">
            {" "}
            · {overdueProjects} overdue
          </span>
        )}
      </span>
      {expanded.size > 0 && (
        <button
          type="button"
          className="gantt-today-btn"
          onClick={() => setExpanded(new Set())}
        >
          Collapse all
        </button>
      )}
    </div>
  );

  if (!range) {
    return (
      <div className="panel gantt">
        <div className="gantt-toolbar">{toolbar}</div>
        <div className="gantt-empty">No projects match these filters.</div>
      </div>
    );
  }

  return (
    <GanttView
      rows={view.rows}
      dependencies={view.dependencies}
      min={range.min}
      max={range.max}
      now={range.now}
      calendar={calendar}
      zoom={zoom || pickDefaultZoom(range.min, range.max)}
      onZoomChange={setZoom}
      leftHeader="Project"
      toolbar={toolbar}
      legend={LEGEND}
      fill
    />
  );
}
